import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  View, Text, StyleSheet, ScrollView, ActivityIndicator, Switch, TextInput, TouchableOpacity, Alert as RNAlert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../src/api/client';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';

// ─── Types ───

interface AlertThreshold {
  label: string;
  unit: string;
  default: number;
  min?: number;
  max?: number;
}

interface AlertSpec {
  label: string;
  description: string;
  group: string;
  defaultSeverity: 'info' | 'warning' | 'critical';
  defaultCooldownMs: number;
  thresholds?: Record<string, AlertThreshold>;
}

interface CatalogResponse {
  groups: { label: string; keys: string[] }[];
  catalog: Record<string, AlertSpec>;
}

interface Pref {
  alertKey: string;
  enabled: boolean;
  thresholdOverride: Record<string, number> | null;
}

const SEVERITY_COLOR: Record<string, string> = {
  info: '#0ea5e9',
  warning: '#d97706',
  critical: '#dc2626',
};

/** AdminAlertPreferences — per-admin subscription editor for every platform alert key. */
export default function AdminAlertPreferences() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [prefsMap, setPrefsMap] = useState<Record<string, Pref>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, prefs] = await Promise.all([
        api<CatalogResponse>('/api/admin/alert-preferences/catalog'),
        api<{ items: Pref[] }>('/api/admin/alert-preferences'),
      ]);
      setCatalog(cat);
      const map: Record<string, Pref> = {};
      for (const p of prefs.items) map[p.alertKey] = p;
      setPrefsMap(map);
      setDirty(false);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const togglePref = (alertKey: string, enabled: boolean) => {
    setPrefsMap(m => ({
      ...m,
      [alertKey]: {
        alertKey,
        enabled,
        thresholdOverride: m[alertKey]?.thresholdOverride ?? null,
      },
    }));
    setDirty(true);
  };

  const setThreshold = (alertKey: string, name: string, value: string) => {
    const num = value === '' ? null : Number(value);
    setPrefsMap(m => {
      const existing = m[alertKey] ?? { alertKey, enabled: false, thresholdOverride: null };
      const override = { ...(existing.thresholdOverride ?? {}) };
      if (num === null || Number.isNaN(num)) delete override[name];
      else override[name] = num;
      return {
        ...m,
        [alertKey]: {
          ...existing,
          thresholdOverride: Object.keys(override).length > 0 ? override : null,
        },
      };
    });
    setDirty(true);
  };

  const handleSave = async () => {
    if (!catalog) return;
    setSaving(true);
    try {
      const prefs = catalog.groups.flatMap(g => g.keys).map(key => ({
        alertKey: key,
        enabled: prefsMap[key]?.enabled ?? false,
        thresholdOverride: prefsMap[key]?.thresholdOverride ?? null,
      }));
      await api('/api/admin/alert-preferences', { method: 'PUT', body: { prefs } });
      setDirty(false);
    } catch (err: any) {
      RNAlert.alert('Save failed', err?.message || 'Could not save preferences');
    } finally { setSaving(false); }
  };

  const handleEnableAll = (groupKeys: string[], enabled: boolean) => {
    setPrefsMap(m => {
      const next = { ...m };
      for (const key of groupKeys) {
        next[key] = {
          alertKey: key,
          enabled,
          thresholdOverride: m[key]?.thresholdOverride ?? null,
        };
      }
      return next;
    });
    setDirty(true);
  };

  const handleTest = async (alertKey: string) => {
    setTesting(alertKey);
    try {
      const res = await api<{ emailsSent: number; recipients: string[]; muted: boolean }>(
        '/api/admin/alert-preferences/test',
        { method: 'POST', body: { alertKey } },
      );
      RNAlert.alert(
        'Test sent',
        res.muted
          ? `Muted (no recipients subscribed).`
          : `${res.emailsSent} email${res.emailsSent === 1 ? '' : 's'} sent to: ${res.recipients.join(', ')}`,
      );
    } catch (err: any) {
      RNAlert.alert('Test failed', err?.message || 'Could not send test alert');
    } finally { setTesting(null); }
  };

  const totalEnabled = useMemo(() => Object.values(prefsMap).filter(p => p.enabled).length, [prefsMap]);

  if (loading || !catalog) {
    return <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>;
  }

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
        <View style={styles.headerRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.headerTitle}>Alert Preferences</Text>
            <Text style={styles.headerSub}>You will receive emails for {totalEnabled} of {Object.keys(catalog.catalog).length} alert types.</Text>
          </View>
          <TouchableOpacity
            style={[styles.saveBtn, !dirty && styles.saveBtnDisabled]}
            disabled={!dirty || saving}
            onPress={handleSave}
          >
            <Ionicons name="save" size={16} color={colors.white} />
            <Text style={styles.saveBtnText}>{saving ? 'Saving…' : 'Save'}</Text>
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={{ paddingBottom: 80 }}>
          {catalog.groups.filter(g => g.keys.length > 0).map(group => {
            const allEnabled = group.keys.every(k => prefsMap[k]?.enabled);
            const someEnabled = group.keys.some(k => prefsMap[k]?.enabled);
            return (
              <View key={group.label} style={styles.groupCard}>
                <View style={styles.groupHeader}>
                  <Text style={styles.groupTitle}>{group.label}</Text>
                  <TouchableOpacity onPress={() => handleEnableAll(group.keys, !allEnabled)}>
                    <Text style={styles.linkText}>
                      {allEnabled ? 'Disable all' : someEnabled ? 'Enable all' : 'Enable all'}
                    </Text>
                  </TouchableOpacity>
                </View>
                {group.keys.map(key => {
                  const spec = catalog.catalog[key];
                  const pref = prefsMap[key];
                  const enabled = pref?.enabled ?? false;
                  return (
                    <View key={key} style={styles.alertRow}>
                      <View style={styles.alertHeader}>
                        <View style={{ flex: 1 }}>
                          <View style={styles.alertTitleRow}>
                            <View style={[styles.sevDot, { backgroundColor: SEVERITY_COLOR[spec.defaultSeverity] }]} />
                            <Text style={styles.alertLabel}>{spec.label}</Text>
                          </View>
                          <Text style={styles.alertDesc}>{spec.description}</Text>
                          <Text style={styles.alertKey}>{key}</Text>
                        </View>
                        <Switch
                          value={enabled}
                          onValueChange={(v) => togglePref(key, v)}
                          trackColor={{ false: colors.borderLight, true: colors.primary }}
                        />
                      </View>
                      {enabled && spec.thresholds && (
                        <View style={styles.thresholdsBox}>
                          {Object.entries(spec.thresholds).map(([thName, th]) => {
                            const current = pref?.thresholdOverride?.[thName];
                            return (
                              <View key={thName} style={styles.thresholdRow}>
                                <Text style={styles.thresholdLabel}>{th.label}</Text>
                                <View style={styles.thresholdInputWrap}>
                                  <TextInput
                                    style={styles.thresholdInput}
                                    keyboardType="numeric"
                                    placeholder={String(th.default)}
                                    placeholderTextColor={colors.textLight}
                                    value={current != null ? String(current) : ''}
                                    onChangeText={(t) => setThreshold(key, thName, t)}
                                  />
                                  <Text style={styles.thresholdUnit}>{th.unit}</Text>
                                </View>
                              </View>
                            );
                          })}
                        </View>
                      )}
                      {enabled && (
                        <TouchableOpacity
                          style={styles.testBtn}
                          disabled={testing === key}
                          onPress={() => handleTest(key)}
                        >
                          <Ionicons name="paper-plane-outline" size={14} color={colors.primary} />
                          <Text style={styles.testBtnText}>{testing === key ? 'Sending…' : 'Send test'}</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  );
                })}
              </View>
            );
          })}
          <PageFooter />
        </ScrollView>
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  headerRow: { flexDirection: 'row', alignItems: 'center', marginBottom: spacing.md, gap: spacing.sm },
  headerTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  headerSub: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 2 },
  saveBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: c.primary, paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderRadius: borderRadius.sm,
  },
  saveBtnDisabled: { opacity: 0.5 },
  saveBtnText: { color: c.white, fontWeight: fontWeight.semibold, fontSize: fontSize.sm },
  groupCard: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md, marginBottom: spacing.md,
  },
  groupHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm },
  groupTitle: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  linkText: { color: c.primary, fontSize: fontSize.sm, fontWeight: fontWeight.medium },
  alertRow: { paddingVertical: spacing.sm, borderTopWidth: 1, borderTopColor: c.borderLight },
  alertHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  alertTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  sevDot: { width: 8, height: 8, borderRadius: 4 },
  alertLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  alertDesc: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  alertKey: { fontSize: 10, color: c.textLight, fontFamily: 'monospace', marginTop: 2 },
  thresholdsBox: { marginTop: spacing.sm, marginLeft: 16, paddingLeft: spacing.sm, borderLeftWidth: 2, borderLeftColor: c.borderLight },
  thresholdRow: { flexDirection: 'row', alignItems: 'center', marginVertical: 4, gap: spacing.sm },
  thresholdLabel: { fontSize: fontSize.xs, color: c.textSecondary, flex: 1 },
  thresholdInputWrap: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  thresholdInput: {
    borderWidth: 1, borderColor: c.borderLight, borderRadius: borderRadius.sm,
    paddingHorizontal: 8, paddingVertical: 4, width: 70, color: c.text, fontSize: fontSize.sm,
  },
  thresholdUnit: { fontSize: fontSize.xs, color: c.textLight, minWidth: 30 },
  testBtn: {
    flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start',
    gap: 4, paddingHorizontal: 10, paddingVertical: 4, borderRadius: borderRadius.sm,
    backgroundColor: c.primaryBg, marginTop: spacing.sm, marginLeft: 16,
  },
  testBtnText: { fontSize: fontSize.xs, color: c.primary, fontWeight: fontWeight.medium },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
